import fetchGraphQL from './api';

export type CaseStudyImage = {
    url: string;
    description?: string;
};

export type CaseStudyFact = {
    label: string;
    value: string;
};

export type CaseStudyDecision = {
    title: string;
    body: string;
    /** The badge label shown beside the cost, e.g. "Tradeoff". */
    badge: string;
    cost: string;
};

export type CaseStudyOutcome = {
    value: string;
    label: string;
};

// The full case study page's content, linked from a caseStudy entry via its `detail` reference.
export type CaseStudyDetail = {
    title: string;
    introText: string;
    facts: CaseStudyFact[];
    problem: string[];
    architectureLabel: string;
    decisions: CaseStudyDecision[];
    outcomes: CaseStudyOutcome[];
    closingText: string;
    cta: string;
};

export type CaseStudyEntry = {
    name: string;
    /** The case study page's URL slug. The page is only linked once `detail` is set too. */
    slug?: string;
    org: string;
    year: string;
    blurb: string;
    tags: string[];
    images: CaseStudyImage[];
    /** The case study page's content, once the project has one. */
    detail?: CaseStudyDetail;
};

/** A project's case study is live, and linked to, once its entry has both a slug and detail. */
export const hasCaseStudyPage = (entry: CaseStudyEntry): boolean => Boolean(entry.slug && entry.detail);

// The JSON decisions field has no badge of its own: every one of them is a tradeoff.
type RawCaseStudyDecision = Omit<CaseStudyDecision, 'badge'>;

// A Step entry, linked from a caseStudyDetail's decisionSteps reference field.
type RawDecisionStep = {
    title?: string | null;
    description?: string | null;
    badgeTitle?: string | null;
    badgeDescription?: string | null;
};

type RawCaseStudyDetail = {
    title: string;
    introText: string;
    facts?: CaseStudyFact[] | null;
    problem?: string[] | null;
    architectureLabel?: string | null;
    decisions?: RawCaseStudyDecision[] | null;
    // Links to unpublished or deleted steps come back as null items.
    decisionStepsCollection?: { items: (RawDecisionStep | null)[] } | null;
    outcomes?: CaseStudyOutcome[] | null;
    closingText?: string | null;
    cta?: string | null;
};

// The shape Contentful returns, before it's mapped onto CaseStudyEntry.
type RawCaseStudy = {
    name: string;
    slug?: string | null;
    org: string;
    year: string;
    blurb: string;
    tags?: string[] | null;
    imagesCollection?: { items: { url: string; description?: string | null }[] } | null;
    detail?: RawCaseStudyDetail | null;
};

// Nested collections multiply their limits (default 100) into the query's cost, which Contentful caps at 11000,
// so the images and decision steps collections are given explicit limits.
const query = `query {
    caseStudyCollection {
        items {
            name
            slug
            org
            year
            blurb
            tags
            imagesCollection(limit: 30) {
                items {
                    url
                    description
                }
            }
            detail {
                title
                introText
                facts
                problem
                architectureLabel
                decisions
                decisionStepsCollection(limit: 20) {
                    items {
                        title
                        description
                        badgeTitle
                        badgeDescription
                    }
                }
                outcomes
                closingText
                cta
            }
        }
    }
  }`;

// Decisions come from the linked steps when there are any, falling back to the JSON decisions field.
const toDecisions = (detail: RawCaseStudyDetail): CaseStudyDecision[] => {
    const steps = (detail.decisionStepsCollection?.items || []).filter((step) => step !== null);
    if (steps.length > 0) {
        return steps.map((step) => ({
            title: step.title || '',
            body: step.description || '',
            badge: step.badgeTitle || '',
            cost: step.badgeDescription || '',
        }));
    }
    return (detail.decisions || []).map((decision) => ({ ...decision, badge: 'Tradeoff' }));
};

export const getCaseStudies = async (): Promise<CaseStudyEntry[]> => {
    const response = await fetchGraphQL<{ caseStudyCollection: { items: RawCaseStudy[] } | null }>(query);

    if (!response || !response.data) {
        throw new Error('Failed to get content response - caseStudyCollection');
    }

    const { caseStudyCollection } = response.data;
    if (!caseStudyCollection) {
        throw new Error('Failed to get caseStudyCollection - no caseStudyCollection');
    }

    return caseStudyCollection.items.map((c) => ({
        name: c.name,
        slug: c.slug || undefined,
        org: c.org,
        year: c.year,
        blurb: c.blurb,
        tags: c.tags || [],
        images: (c.imagesCollection?.items || []).map((image) => ({
            url: image.url,
            description: image.description || '',
        })),
        detail: c.detail
            ? {
                  title: c.detail.title,
                  introText: c.detail.introText,
                  facts: c.detail.facts || [],
                  problem: c.detail.problem || [],
                  architectureLabel: c.detail.architectureLabel || '',
                  decisions: toDecisions(c.detail),
                  outcomes: c.detail.outcomes || [],
                  closingText: c.detail.closingText || '',
                  cta: c.detail.cta || '',
              }
            : undefined,
    }));
};
